import { useEffect, useMemo, useState } from 'react';
import { Input } from '../../../../components/ui/atoms/input';
import { Label } from '../../../../components/ui/atoms/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../../components/ui/molecules/select';
import { SegmentedToggle } from '../components';
import { RangeFilterProps, RangeMode } from '../interface';
import {
  RANGE_MODES, availableYears, periodOptions, weekOptions, normalizeCustom, periodId, parsePeriodId,
  resolveRange, rangeIssue,
} from '../utils';

const ALL = '__all';

/**
 * Year / Quarter / Month to Date; a custom pick in three levels (year, then a
 * half, quarter or month, then a week), each offering only what lies inside the
 * one above and nothing that starts after today; or a free From / To range.
 */
export function RangeFilter({ value, onChange, now }: RangeFilterProps) {
  const today = useMemo(() => now ?? new Date(), [now]);
  const years = useMemo(() => availableYears(today), [today]);

  const custom = value.kind === 'custom' ? value : null;
  const periods = useMemo(() => (custom ? periodOptions(custom.year, today) : []), [custom, today]);
  const weeks = useMemo(
    () => (custom ? weekOptions(custom.year, custom.period, today) : []),
    [custom, today],
  );

  // A range is typed in two boxes, so what is on screen can be half-finished. Only a
  // pair that passes the checks is handed on; the rest stays here with a message.
  const rangeFrom = value.kind === 'range' ? value.from : '';
  const rangeTo = value.kind === 'range' ? value.to : '';
  const [draft, setDraft] = useState({ from: rangeFrom, to: rangeTo });
  useEffect(() => setDraft({ from: rangeFrom, to: rangeTo }), [rangeFrom, rangeTo]);
  const todayIso = today.toISOString().slice(0, 10);
  const issue = value.kind === 'range' ? rangeIssue(draft.from, draft.to, today) : null;

  const handleDate = (key: 'from' | 'to', date: string) => {
    const next = { ...draft, [key]: date };
    setDraft(next);
    if (rangeIssue(next.from, next.to, today) === null) onChange({ kind: 'range', ...next });
  };

  const handleMode = (mode: RangeMode) => {
    if (mode === 'custom') {
      if (!custom) onChange({ kind: 'custom', year: years[0] });
      return;
    }
    if (mode === 'range') {
      // Start from the dates on screen, so switching changes nothing until the user does.
      if (value.kind !== 'range') onChange({ kind: 'range', ...resolveRange(value, today) });
      return;
    }
    onChange({ kind: 'preset', mode });
  };

  // Changing a level re-checks the ones below it, so none is left pointing outside it.
  const change = (changes: { year?: number; period?: string; week?: string }) => {
    if (!custom) return;
    onChange(normalizeCustom({
      kind: 'custom',
      year: changes.year ?? custom.year,
      period: changes.period === undefined ? custom.period : (changes.period === ALL ? undefined : parsePeriodId(changes.period)),
      week: changes.week === undefined ? custom.week : (changes.week === ALL ? undefined : Number(changes.week)),
    }, today));
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1">
        <Label className="text-xs">Date range</Label>
        <SegmentedToggle
          label="Date range"
          value={value.kind === 'preset' ? value.mode : value.kind}
          options={RANGE_MODES}
          onChange={handleMode}
        />
      </div>

      {custom && (
        <>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="range-year">Year</Label>
            <Select value={String(custom.year)} onValueChange={(v) => change({ year: Number(v) })}>
              <SelectTrigger id="range-year" className="h-9 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="range-period">Period</Label>
            <Select
              value={custom.period ? periodId(custom.period) : ALL}
              onValueChange={(v) => change({ period: v })}
            >
              <SelectTrigger id="range-period" className="h-9 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Whole year</SelectItem>
                {periods.map((p) => (
                  <SelectItem key={p.id} value={p.id} disabled={p.disabled}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs" htmlFor="range-week">Week</Label>
            <Select
              value={custom.week === undefined ? ALL : String(custom.week)}
              onValueChange={(v) => change({ week: v })}
            >
              <SelectTrigger id="range-week" className="h-9 w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All weeks</SelectItem>
                {weeks.map((w) => (
                  <SelectItem key={w.week} value={String(w.week)} disabled={w.disabled}>{w.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {value.kind === 'range' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="range-from">From</Label>
            <Input
              id="range-from"
              type="date"
              value={draft.from}
              max={todayIso}
              onChange={(e) => handleDate('from', e.target.value)}
              className="h-9 w-40"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="range-to">To</Label>
            <Input
              id="range-to"
              type="date"
              value={draft.to}
              max={todayIso}
              onChange={(e) => handleDate('to', e.target.value)}
              className="h-9 w-40"
            />
          </div>
          {issue && <p role="alert" className="pb-2 text-xs text-destructive">{issue}</p>}
        </>
      )}
    </div>
  );
}
